import { FieldPasswordCompact, InfoTooltip } from '@stratumn/atomic';

import classnames from 'classnames';
import debounce from 'debounce';
import PropType from 'prop-types';
import React from 'react';
import injectSheet from 'react-jss';

import styles from './changePassword.style';

const PASSWORD_CHECK_DELAY = 100; // ms
const MINIMUM_SCORE = 4;

const atLeastOneNumber = /[0-9]/;
const atLeastOneUpper = /[A-Z]/;
const atLeastOneSpecial = /[!@$%^&(){}[\]:;<>,\-+.?/~_=|]/;
const atLeast8CharLong = /^.{8,32}$/;

export class ChangePassword extends React.PureComponent {
  static propTypes = {
    onChange: PropType.func.isRequired,
    extraExclusionTerms: PropType.arrayOf(PropType.string),
    classes: PropType.object.isRequired,
    labelPassword: PropType.string,
    labelConfirmPassword: PropType.string,
    disableRules: PropType.bool
  };

  static defaultProps = {
    extraExclusionTerms: [],
    labelPassword: 'Password',
    labelConfirmPassword: 'Confirm password',
    disableRules: false
  };

  state = {
    // reflect the last values that have been validated
    password: '',
    confirmation: '',

    // reflect the actual input fields
    tempPassword: '',
    tempConfirmation: '',

    showPassword: false
  };

  validatePassword = debounce(() => {
    const { tempConfirmation, tempPassword } = this.state;
    const { onChange, disableRules } = this.props;
    this.setState({
      password: tempPassword,
      confirmation: tempConfirmation
    });

    if (disableRules) {
      if (tempConfirmation === tempPassword && tempPassword.length > 0) {
        onChange(tempPassword);
      } else {
        onChange(null);
      }
      return;
    }

    if (
      this.passwordScore(tempPassword) === MINIMUM_SCORE &&
      tempConfirmation === tempPassword
    ) {
      onChange(tempPassword);
    } else {
      onChange(null);
    }
  }, PASSWORD_CHECK_DELAY);

  componentWillUnmount() {
    this.validatePassword.clear();
  }

  togglePassword = () =>
    this.setState({ showPassword: !this.state.showPassword });

  handlePasswordChange = event => {
    const {
      target: { value: password }
    } = event;
    this.props.onChange(null);
    this.setState({ tempPassword: password });
    this.validatePassword();
  };

  handleConfirmationChange = event => {
    const {
      target: { value: confirmation }
    } = event;
    this.props.onChange(null);
    this.setState({ tempConfirmation: confirmation });
    this.validatePassword();
  };

  getBarClasses = (idx, score) => {
    const { classes } = this.props;
    const { password } = this.state;

    if (
      !password ||
      password.length === 0 ||
      score === null ||
      idx > score - 1 ||
      score === 0
    )
      return classes.bar;

    return classnames(classes.bar, {
      [classes.greenBG]: score === 4,
      [classes.orangeBG]: score === 3,
      [classes.redBG]: score === 2 || score === 1
    });
  };

  renderStrengthBar = score => {
    const { classes, disableRules } = this.props;
    if (disableRules) return null;
    return (
      <div className={classes.strengthBar}>
        {Array.from(Array(4), (_, idx) => (
          <div className={this.getBarClasses(idx, score)} key={idx} />
        ))}
      </div>
    );
  };

  passwordScore = tempPassword =>
    atLeast8CharLong.test(tempPassword) +
    atLeastOneNumber.test(tempPassword) +
    atLeastOneSpecial.test(tempPassword) +
    atLeastOneUpper.test(tempPassword);

  renderPasswordRules = () => {
    const { tempPassword } = this.state;
    const { classes, disableRules } = this.props;
    if (disableRules) return null;

    return (
      <React.Fragment>
        {atLeast8CharLong.test(tempPassword) ? (
          <React.Fragment />
        ) : (
          <p>Your password needs to be at least 8 characters long</p>
        )}
        {atLeastOneNumber.test(tempPassword) ? (
          <React.Fragment />
        ) : (
          <p>Your password needs to have at least 1 number</p>
        )}
        {atLeastOneUpper.test(tempPassword) ? (
          <React.Fragment />
        ) : (
          <p>Your password needs to have at least 1 upper char</p>
        )}
        {atLeastOneSpecial.test(tempPassword) ? (
          <React.Fragment />
        ) : (
          <div className={classes.tooltipContainer}>
            <p>Your password needs to have at least 1 special char</p>
            <InfoTooltip
              backgroundColor="indigo3"
              text="Special characters: ! @ $ % ^ & ( ) { } [ ] : ; < > , - + . ? / ~ _ = |"
              textAlign="center"
              textColor="#fff"
            >
              <button
                aria-label="Show special character requirements"
                className={classes.tooltipButton}
                type="button"
              >
                ?
              </button>
            </InfoTooltip>
          </div>
        )}
      </React.Fragment>
    );
  };

  render() {
    const { classes, labelPassword, labelConfirmPassword, disableRules } =
      this.props;
    const {
      password,
      tempPassword,
      confirmation,
      tempConfirmation,
      showPassword
    } = this.state;

    const confirmationError = confirmation && confirmation !== password;

    const isNeutral = !password || password.length === 0;

    const score = this.passwordScore(tempPassword);

    const isInvalid = disableRules
      ? false // If rules are disabled, we don't show invalid state based on score
      : !isNeutral && score !== MINIMUM_SCORE;

    const isValid = disableRules
      ? !isNeutral && tempConfirmation === tempPassword // Simple validation
      : !isNeutral && score === MINIMUM_SCORE;

    return (
      <React.Fragment>
        <div className={classes.passwordField} data-testid="new-password">
          <FieldPasswordCompact
            data-testid="password-field"
            invalid={isInvalid}
            label={labelPassword}
            onValueChange={this.handlePasswordChange}
            onVisibilityChange={this.togglePassword}
            passwordIsVisible={showPassword}
            valid={isValid}
            value={tempPassword}
          />
          {this.renderStrengthBar(score)}
          <div className={classes.password_rules}>
            {this.renderPasswordRules()}
            {!disableRules && score === 4 ? (
              <p>Your password is strong enough!</p>
            ) : (
              <React.Fragment />
            )}
          </div>
        </div>
        <div
          className={classes.confirmPasswordField}
          data-testid="new-password-confirmation"
        >
          <FieldPasswordCompact
            data-testid="confirmation-field"
            invalid={!!confirmationError}
            label={labelConfirmPassword}
            onValueChange={this.handleConfirmationChange}
            onVisibilityChange={this.togglePassword}
            passwordIsVisible={showPassword}
            value={tempConfirmation}
          />
          {confirmationError ? (
            <div className={classes.redText}>Your passwords do not match.</div>
          ) : (
            <div className={classes.hiddenText}>____</div>
          )}
        </div>
      </React.Fragment>
    );
  }
}

export default injectSheet(styles)(ChangePassword);
